import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import 'react-datepicker/dist/react-datepicker.css';
import { jwtDecode } from 'jwt-decode';
import { GetRecentlySearchedPamcodesAsync, RecentSearchData } from "../../Admin-Api/GetRecentlySearchedPamcodesAsync";
import DatePicker from "react-datepicker";
import { set } from "react-datepicker/dist/date_utils";

interface CustomJwtPayload {
    RoleId: string;
}

const RecentHistory: React.FC = () => {
    const navigate = useNavigate();
    const [fromDate, setFromDate] = useState<Date | null>(null);
    const [toDate, setToDate] = useState<Date | null>(null);
    const [recentSearchData, setRecentSearchData] = useState<RecentSearchData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            toast.error("No auth token found. Please log in.");
            navigate('/');
        } else {
            try {
                const decodedToken = jwtDecode<CustomJwtPayload>(token);
                if (decodedToken.RoleId !== "1") {
                    toast.error("Unauthorized access: RoleId is not 1");
                    navigate('/');
                }
            } catch (error) {
                toast.error("Invalid token. Please log in again.");
                navigate('/');
            }
        }
    }, [navigate]);

    const formatDate = (date: Date | null) => {
        if (!date) return "";
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
   
    const formatDisplayDate = (date: Date | null) => {
        if (!date) return "";
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const formatDateString = (dateString: string): string => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const formatPamCode = (pinCode: string, blockCode: string, addressCode: string) => {
        return `${pinCode}-${blockCode}-${addressCode}`;
    };

    const formatDateRange = (startDate: Date | null, endDate: Date | null) => {
        if (!startDate || !endDate) return "";
        if (startDate.getTime() === endDate.getTime()) {
            return formatDisplayDate(startDate);
        }
        return `${formatDisplayDate(startDate)} to ${formatDisplayDate(endDate)}`;
    };

    const fetchRecentSearchData = async (fromDateStr: string, toDateStr: string) => {
    setIsLoading(true);
    setHasSearched(true);

    try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No auth token found');

        const data = await GetRecentlySearchedPamcodesAsync(
            fromDateStr,
            toDateStr,
            token
        );

        // Sort latest records first
        const sortedData = [...data].sort(
            (a, b) =>
                new Date(b.createdDate).getTime() -
                new Date(a.createdDate).getTime()
        );

        setRecentSearchData(sortedData);

        if (sortedData.length === 0) {
            // toast.info("No recent search data found");
        }

    } catch (error) {
        console.error('Error fetching recent search data:', error);
    } finally {
        setIsLoading(false);
    }
};

    const handleSearch = () => {
        if (!fromDate || !toDate) {
            if (!toast.isActive("select-dates")) {
                toast.info("Please select both from and to dates.", { toastId: "select-dates" });
            }
            return;
        }

        if (fromDate > toDate) {
            if (!toast.isActive("invalid-range")) {
                toast.error("From date cannot be after to date.", { toastId: "invalid-range" });
            }
            return;
        }

        fetchRecentSearchData(formatDate(fromDate), formatDate(toDate));
    };

    const handleFromDateChange = (date: Date | null) => {
        setFromDate(date);
        setHasSearched(false);
        setRecentSearchData([]);
    };

    const handleToDateChange = (date: Date | null) => {
        setToDate(date);
        setHasSearched(false);
        setRecentSearchData([]);
    };
    
    return (
        <main className="flex-1 overflow-auto px-4 sm:px-6 py-6">
            <div className="max-w-7xl mx-auto">
                <div>
                    <h4 className="text-base sm:text-lg text-dark font-semibold mb-4">
                        Recent History
                        {recentSearchData.length > 0 && (
                            <span className="text-xl text-blue-600 ml-2 font-bold">
                                (Total: {recentSearchData.length})
                            </span>
                        )}
                    </h4>

                    <div className="flex flex-col sm:flex-row gap-4 mt-4 items-start">
                        <div className="w-full sm:w-auto">
                            <label className="block text-sm font-medium text-gray-700">Start Date</label>
                            <DatePicker
                                selected={fromDate}
                                onChange={handleFromDateChange}
                                maxDate={toDate || new Date()}
                                dateFormat="dd-MM-yyyy"
                                placeholderText="dd-mm-yyyy"
                                className="border rounded p-2 w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>

                        <div className="w-full sm:w-auto">
                            <label className="block text-sm font-medium text-gray-700">End Date</label>
                            <DatePicker
                                selected={toDate}
                                onChange={handleToDateChange}
                                minDate={fromDate ?? undefined}
                                maxDate={new Date()}
                                dateFormat="dd-MM-yyyy"
                                placeholderText="dd-mm-yyyy"
                                className="border rounded p-2 w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>

                        <button
                            onClick={handleSearch}
                            disabled={isLoading}
                            className="bg-indigo-600 mt-5 text-white px-4 h-10 rounded-md hover:bg-indigo-700 disabled:bg-indigo-400 transition-colors"
                        >
                            {isLoading ? 'Searching...' : 'Search'}
                        </button>
                    </div>

                    {isLoading && (
                        <div className="mt-4 flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600 mr-2"></div>
                            <p className="text-gray-500">Loading recent search data...</p>
                        </div>
                    )}

                    {recentSearchData.length > 0 && (
                        <div className="mt-6">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
                                <h4 className="text-lg font-semibold">
                                    Recently Added Pam Codes ({formatDateRange(fromDate, toDate)})
                                </h4>
                            </div>

                            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                                <div className="p-6">
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-blue-600">
                                                <tr>
                                                    <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider rounded-tl-md">
                                                        S.No
                                                    </th>
                                                    <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                                                        PAM Code
                                                    </th>
                                                    <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                                                        Address
                                                    </th>
                                                    <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">
                                                        Address Type
                                                    </th>
                                                    <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider rounded-tr-md">
                                                        Added on
                                                    </th>
                                                    
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {recentSearchData.map((item, index) => (
                                                    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                                                            {index + 1}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                                            <div className="text-sm font-medium text-blue-600">
                                                                {formatPamCode(item.pinCode, item.blockCode, item.addressCode)}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                                                            <div className="truncate">
                                                                {item.addressLine1 && `${item.addressLine1}, `}
                                                                {item.addressLine2 !== "N/A" && item.addressLine2 && `${item.addressLine2}, `}
                                                                {item.addressLine3}
                                                            </div>
                                                            <div className="text-xs text-gray-500">
                                                                Pin: {item.pinCode}
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                                            <span className="inline-flex px-2 py-1 text-sm font-semibold rounded-full  text-blue-800">
                                                                {item.addressType}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                                                            {formatDateString(item.createdDate)}
                                                        </td>
                                                        
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {!isLoading && hasSearched && fromDate && toDate && recentSearchData.length === 0 && (
                        <div className="mt-6 p-6 bg-white rounded-lg shadow-md text-center">
                            <div className="text-gray-400 mb-2">
                                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <p className="text-gray-500 text-lg font-medium">
                                No recent search data found
                            </p>
                            <p className="text-gray-400 text-sm">
                                for {formatDateRange(fromDate, toDate)}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
};

export default RecentHistory;
